import { watch, FSWatcher } from 'chokidar';
import path from 'path';
import fs from 'fs-extra';
import { Task, AnalysisResult } from '../types';
import { TaskAnalyzer } from './task-analyzer';
import { parseTasks, generateTaskList } from '../utils/markdown';
import { logger } from '../utils/logger';

export interface TaskWatcherOptions {
  projectRoot: string;
  todoPath: string;
  workflowDir: string;
  syncInterval?: number;
}

export class TaskWatcher {
  private watcher: FSWatcher | null = null;
  private options: TaskWatcherOptions;
  private analyzer: TaskAnalyzer;
  private lastSync: Date | null = null;
  private syncTimer: NodeJS.Timeout | null = null;

  constructor(options: TaskWatcherOptions) {
    this.options = {
      syncInterval: 300000, // 5 minutes default
      ...options
    };
    this.analyzer = new TaskAnalyzer();
  }

  async start(): Promise<void> {
    if (this.watcher) {
      logger.warn('Task watcher is already running');
      return;
    }

    // Initial sync
    await this.sync();

    // Start watching
    this.watcher = watch(this.options.todoPath, {
      persistent: true,
      ignoreInitial: true,
      awaitWriteFinish: {
        stabilityThreshold: 1000,
        pollInterval: 100
      }
    });

    this.watcher.on('change', async () => {
      logger.info('📝 TODO file changed, syncing tasks...');
      await this.sync();
    });

    this.watcher.on('error', (error) => {
      logger.error('Watch error:', error);
    });

    // Set up periodic sync
    if (this.options.syncInterval > 0) {
      this.syncTimer = setInterval(
        () => this.sync(),
        this.options.syncInterval
      );
    }
  }

  async stop(): Promise<void> {
    if (this.watcher) {
      await this.watcher.close();
      this.watcher = null;
    }

    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }

  async syncOnce(): Promise<void> {
    await this.sync();
  }

  private async sync(): Promise<void> {
    try {
      // Read TODO file
      const todoContent = await fs.readFile(this.options.todoPath, 'utf-8');
      
      // Analyze tasks
      const analysis = await this.analyzer.analyze(todoContent);
      
      // Generate dynamic content
      const dynamicContent = this.generateDynamicContent(analysis);
      
      // Write to workflow commands
      const todoCommandPath = path.join(this.options.workflowDir, 'commands', 'todo.md');
      await fs.writeFile(todoCommandPath, dynamicContent);
      
      // Update activity log
      await this.updateActivityLog(
        `Synced ${analysis.statistics.total} tasks (${analysis.statistics.completed} completed)`
      );
      
      this.lastSync = new Date();
      logger.debug(`Tasks synced at ${this.lastSync.toLocaleTimeString()}`);
    } catch (error: any) {
      logger.error('Failed to sync tasks:', error.message);
    }
  }

  private generateDynamicContent(analysis: AnalysisResult): string {
    const lines: string[] = [];
    const now = new Date();
    
    lines.push('# 📋 Dynamic Task Management');
    lines.push('');
    lines.push(`> Last synced: ${now.toLocaleString()}`);
    lines.push(`> Source: ${path.relative(this.options.projectRoot, this.options.todoPath)}`);
    lines.push('');
    
    // Add recommendations
    if (analysis.recommendations.length > 0) {
      lines.push('## 🤖 AI Recommendations');
      lines.push('');
      analysis.recommendations.forEach(rec => {
        lines.push(`### ${rec.title}`);
        lines.push(`${rec.description}`);
        if (rec.action) {
          lines.push(`**Action**: ${rec.action}`);
        }
        lines.push('');
      });
    }
    
    // Add insights
    if (analysis.insights.trends.length > 0 || 
        analysis.insights.bottlenecks.length > 0 || 
        analysis.insights.suggestions.length > 0) {
      lines.push('## 💡 Insights');
      lines.push('');
      
      if (analysis.insights.trends.length > 0) {
        lines.push('### Trends');
        analysis.insights.trends.forEach(trend => {
          lines.push(`- ${trend}`);
        });
        lines.push('');
      }
      
      if (analysis.insights.bottlenecks.length > 0) {
        lines.push('### Bottlenecks');
        analysis.insights.bottlenecks.forEach(bottleneck => {
          lines.push(`- ${bottleneck}`);
        });
        lines.push('');
      }
      
      if (analysis.insights.suggestions.length > 0) {
        lines.push('### Suggestions');
        analysis.insights.suggestions.forEach(suggestion => {
          lines.push(`- ${suggestion}`);
        });
        lines.push('');
      }
    }
    
    // Add task list
    lines.push(generateTaskList(analysis.tasks));
    
    // Add quick actions
    lines.push('');
    lines.push('## 🚀 Quick Actions');
    lines.push('');
    lines.push('```bash');
    lines.push('# Sync with latest TODO file');
    lines.push('workflow sync');
    lines.push('');
    lines.push('# Start watching for changes');
    lines.push('workflow sync --watch');
    lines.push('');
    lines.push('# Start a new feature');
    lines.push('workflow start feature');
    lines.push('```');
    
    return lines.join('\n');
  }

  private async updateActivityLog(message: string): Promise<void> {
    const logPath = path.join(this.options.workflowDir, 'logs', 'activity.log');
    await fs.ensureDir(path.dirname(logPath));
    
    const timestamp = new Date().toISOString();
    const logEntry = `[${timestamp}] ${message}\n`;
    
    await fs.appendFile(logPath, logEntry);
  }
}